import React from 'react';
import { Link } from 'react-router-dom';
import FlightStatus from './FlightStatus';
import { formatTime24, formatDateLocal, formatDateReadable } from '../utils/dateTime';

// All booked travel for a trip in one chronological list, to verify imports
// (flights/times/numbers) at a glance. Reuses the same data the itinerary loads.

const typeIcon = (name) => {
  const n = String(name || '').toLowerCase();
  if (n === 'flight') return '✈';
  if (n.includes('train') || n.includes('rail')) return '🚆';
  if (n.includes('ferry') || n.includes('boat') || n.includes('cruise')) return '⛴';
  if (n.includes('bus') || n.includes('coach')) return '🚌';
  return '→';
};

function whenLabel(iso, tz) {
  if (!iso) return '—';
  const local = formatDateLocal(iso, tz || 'UTC');
  const readable = formatDateReadable(local) || local;
  const time = formatTime24(iso, tz || 'UTC');
  return time ? `${readable} · ${time}` : readable;
}

export default function TravelOverview({ tripId, activities, activityTypes, isTravelTypeId, onDelete }) {
  const typeName = (typeId) =>
    (Array.isArray(activityTypes) ? activityTypes : []).find(t => String(t.type_id) === String(typeId))?.type_name || '';

  const travel = (Array.isArray(activities) ? activities : []).filter(
    a => isTravelTypeId(a.type_id) || (Array.isArray(a.legs) && a.legs.length) || a.from_address || a.to_address
  );
  const depKey = (a) =>
    String(a.legs?.[0]?.depart_time || a.start_time || a.legs?.[0]?.depart_local_date || '');
  const sorted = [...travel].sort((a, b) => depKey(a).localeCompare(depKey(b)));

  if (!sorted.length) {
    return (
      <section className="tp-section">
        <div className="tp-card tp-card--tight" style={{ textAlign: 'center', color: 'var(--tp-muted)', padding: 28 }}>
          No travel booked yet. Use <strong>Import Flights</strong> to add flights from a PDF or .ics file.
        </div>
      </section>
    );
  }

  return (
    <section className="tp-section">
      <div className="tp-row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <h2 className="tp-section__title" style={{ margin: 0 }}>Booked travel</h2>
        <span className="tp-pill">{sorted.length} item{sorted.length === 1 ? '' : 's'}</span>
      </div>

      {sorted.map(act => {
        const tname = typeName(act.type_id);
        const legs = (Array.isArray(act.legs) && act.legs.length)
          ? [...act.legs].sort((x, y) => (Number(x.leg_index) || 0) - (Number(y.leg_index) || 0))
          : [{
              leg_index: 0, airline: act.airline,
              from_address: act.from_address, from_city: act.from_city,
              to_address: act.to_address, to_city: act.to_city,
              depart_time: act.start_time, arrive_time: act.end_time, from_tz: 'UTC', to_tz: 'UTC',
            }];
        const transfers = Array.isArray(act.transfers) ? act.transfers : [];

        return (
          <div key={act.activity_id} className="tp-card tp-card--tight" style={{ marginBottom: 12 }}>
            <div className="tp-row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ fontWeight: 700 }}>{typeIcon(tname)} {act.title || 'Travel'}</div>
              <div className="tp-row" style={{ gap: 10 }}>
                <Link className="tp-link" to={`/itinerary/${tripId}/activity/${act.activity_id}`} style={{ fontSize: 13 }}>Open ↗</Link>
                <button
                  type="button"
                  className="tp-btn--ghost"
                  style={{ fontSize: 13 }}
                  onClick={() => { if (window.confirm(`Delete "${act.title || 'this travel'}"?`)) onDelete(act.activity_id); }}
                >
                  Delete
                </button>
              </div>
            </div>
            {act.booking_ref && (
              <div style={{ fontSize: 12, color: 'var(--tp-muted)', marginTop: 2 }}>Confirmation: {act.booking_ref}</div>
            )}

            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {legs.map((leg, i) => {
                const xfer = transfers.find(t => Number(t.from_leg_index) === Number(leg.leg_index ?? i));
                return (
                  <React.Fragment key={leg.leg_id || i}>
                    <div style={{ borderLeft: '2px solid var(--tp-border)', paddingLeft: 12 }}>
                      {[leg.airline, leg.flight_number].filter(Boolean).length > 0 && (
                        <div style={{ fontSize: 13, fontWeight: 600 }}>
                          {[leg.airline, leg.flight_number].filter(Boolean).join(' ')}
                        </div>
                      )}
                      <div style={{ fontSize: 14, marginTop: 2 }}>
                        {leg.from_address || leg.from_city || '—'}
                        <span style={{ color: 'var(--tp-muted)' }}> → </span>
                        {leg.to_address || leg.to_city || '—'}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--tp-muted)', marginTop: 2 }}>
                        Dep {whenLabel(leg.depart_time, leg.from_tz)} &nbsp;·&nbsp; Arr {whenLabel(leg.arrive_time, leg.to_tz)}
                      </div>
                      {leg.flight_number && (
                        <FlightStatus
                          flightNumber={leg.flight_number}
                          date={String(leg.depart_time || '').slice(0, 10)}
                          label={`${leg.airline ? leg.airline + ' ' : ''}${leg.flight_number}`}
                        />
                      )}
                    </div>
                    {xfer && (
                      <div style={{ fontSize: 12, color: 'var(--tp-muted)', paddingLeft: 12 }}>
                        ↕ {xfer.kind === 'layover' ? 'Layover' : 'Connection'} · {Math.floor(xfer.minutes / 60)}h {xfer.minutes % 60}m
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        );
      })}
    </section>
  );
}
